'use client'

import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Button, Cell, Container, Dialog, Spacer, Text } from '@vinted/web-ui'
import { QuestionCircle24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  clickEvent,
  viewEvent,
  viewScreenEvent,
  BumpOptionModel,
  BumpOptionsModel,
} from '@marketplace-web/vas/vas-data'

import BumpOptionList from './BumpOptionList'
import { ItemSelection } from '../../ItemSelection'
import { generateBumpMotivationText } from '../../../../utils/bumpMotivationText'

enum ReviewOrderActionTargetDetails {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

type Props = {
  show: boolean
  isOrderLoading: boolean
  isBumpOptionsLoading: boolean
  bumpOptions?: BumpOptionsModel
  selectedOption: BumpOptionModel | null
  onSelectedOptionChange: (selectedBumpOption: BumpOptionModel) => void
  onAddButtonClick: () => void
  onConfirm: () => void
  onCancel: () => void
  onHelpButtonClick: () => void
}

const BumpSelectionConfirmationModal = ({
  show,
  isOrderLoading,
  isBumpOptionsLoading,
  bumpOptions,
  selectedOption,
  onSelectedOptionChange,
  onCancel,
  onConfirm,
  onAddButtonClick,
  onHelpButtonClick,
}: Props) => {
  const { locale } = useIntl()

  const isEmptyBumpOrderFixEnabled = useFeatureSwitch('empty_bump_order_fix_web')

  const { track } = useTracking()
  const translate = useTranslate('bump_items.selection_confirmation_modal')
  const a11yTranslate = useTranslate('bump_items.a11y')

  const orderItemsCount = selectedOption?.bumpOrderItems.length

  useEffect(() => {
    if (!show) return

    track(
      viewScreenEvent({
        screen: 'push_up_order_review',
      }),
    )
  }, [show, track])

  useEffect(() => {
    if (!isEmptyBumpOrderFixEnabled) return
    if (orderItemsCount === undefined) return

    track(
      viewEvent({
        screen: 'push_up_order_review',
        target: 'bump_precheckout_review_order_action',
        targetDetails: orderItemsCount
          ? ReviewOrderActionTargetDetails.Enabled
          : ReviewOrderActionTargetDetails.Disabled,
      }),
    )
  }, [orderItemsCount, isEmptyBumpOrderFixEnabled, track])

  function handleConfirm(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (isOrderLoading || isBumpOptionsLoading) {
      event.preventDefault()

      return
    }

    if (isOrderLoading || !selectedOption) return

    onConfirm()

    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'review_push_up_order',
        targetDetails: selectedOption.days.toString(),
      }),
    )
  }

  function handleCancel() {
    onCancel()

    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'close_screen',
      }),
    )
  }

  function handleAddButtonClick() {
    onAddButtonClick()
    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'multiple_push_up',
      }),
    )
  }

  function handleHelpButtonClick() {
    onHelpButtonClick()
    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'push_up_learn_more',
      }),
    )
  }

  function handleBumpOptionChange(selectedBumpOption: BumpOptionModel) {
    onSelectedOptionChange(selectedBumpOption)
    track(
      clickEvent({
        screen: 'push_up_order_review',
        target: 'push_up_duration_change',
        targetDetails: JSON.stringify({
          dutation: selectedBumpOption.days.toString(),
          is_international: selectedBumpOption.international,
        }),
      }),
    )
  }

  function getOrderPriceLabel() {
    return selectedOption ? formatCurrencyAmount(selectedOption.discountedPrice, locale) : ''
  }

  function renderTitle() {
    return (
      <Cell
        styling="narrow"
        prefix={<Spacer orientation="horizontal" size="x3-large" />}
        suffix={
          <Button
            iconName={QuestionCircle24}
            styling="flat"
            onClick={handleHelpButtonClick}
            testId="bump-help-button"
            aria={{ 'aria-label': a11yTranslate('actions.learn_more') }}
          />
        }
      >
        <Text
          as="h2"
          id="bump_selection_modal_title"
          type="title"
          alignment="center"
          text={translate('title')}
          width="parent"
        />
      </Cell>
    )
  }

  function renderMotivation() {
    if (!bumpOptions) return null
    if (!orderItemsCount) return null

    const motivationText = generateBumpMotivationText({
      bumpOptions,
      orderItemsCount,
      translate,
    })

    if (!motivationText) return null

    return (
      <Cell styling="narrow">
        <Text
          as="span"
          format
          type="subtitle"
          width="parent"
          alignment="center"
          text={motivationText}
          theme="success"
          testId="bump-motivation-label"
        />
      </Cell>
    )
  }

  function renderBumpOptions() {
    if (!bumpOptions?.options.length || !selectedOption) return null

    return (
      <BumpOptionList
        bumpOptions={bumpOptions.options}
        selectedOptionId={selectedOption.id}
        onSelect={handleBumpOptionChange}
        isLoading={isBumpOptionsLoading}
      />
    )
  }

  function renderSalesTaxNote() {
    if (!selectedOption?.isSalesTaxApplicable) return null

    return (
      <Text
        as="span"
        alignment="center"
        text={translate('duration.details.sales_tax_note')}
        testId="bump-selection-confirmation-modal--sales-tax-note"
        width="parent"
      />
    )
  }

  function renderBody() {
    if (!bumpOptions) return null

    return (
      <Container>
        <ItemSelection
          orderItems={selectedOption?.bumpOrderItems}
          onAddButtonClick={handleAddButtonClick}
        />
        <Spacer size="large" />
        <SeparatedList separator={<Spacer orientation="horizontal" />}>
          {renderBumpOptions()}
          {renderSalesTaxNote()}
        </SeparatedList>
      </Container>
    )
  }

  function renderActions() {
    const amount = getOrderPriceLabel()
    const isEmptyOrder = isEmptyBumpOrderFixEnabled && !orderItemsCount

    return (
      <Cell>
        <Button
          isLoading={isOrderLoading || isBumpOptionsLoading}
          disabled={!selectedOption || isBumpOptionsLoading || isEmptyOrder}
          styling="filled"
          text={translate('actions.review_order', { amount })}
          onClick={handleConfirm}
          testId="bump-review-order-button"
        />
        <Spacer />
        <Button styling="flat" text={translate('actions.cancel')} onClick={handleCancel} />
      </Cell>
    )
  }

  return (
    <Dialog
      aria={{ labelledby: 'bump_selection_modal_title' }}
      show={show}
      hasScrollableContent
      testId="bump-selection-confirmation-modal"
    >
      <div className="u-fill-width">
        {renderTitle()}
        <ScrollableArea>
          {renderMotivation()}
          {renderBody()}
        </ScrollableArea>
        {renderActions()}
      </div>
    </Dialog>
  )
}

export default BumpSelectionConfirmationModal
